import styled from 'styled-components';

import { useClickAttachment } from 'hooks/useClickAttachment';
import colors from 'styles/theme/colors';
import textStyles from 'styles/theme/textStyles';

export type AdmissionsCriteriaColor = 'green' | 'yellow' | 'red' | 'orange' | 'blue';

export type AdmissionSource = {
  attachmentId: string;
  displayId: number;
};

export type AdmissionsCriterion = {
  label: string;
  description?: string;
  sourceCount: number;
  sources?: AdmissionSource[];
};

export type AdmissionsCriteriaProps = {
  categoryLabel: string;
  color: AdmissionsCriteriaColor;
  criteria: AdmissionsCriterion[];
};

export const colorConfig: Record<AdmissionsCriteriaColor, { accent: string; background: string }> = {
  green: {
    accent: colors.green100,
    background: colors.green5,
  },
  yellow: {
    accent: colors.yellow100,
    background: colors.yellow5,
  },
  red: {
    accent: colors.red100,
    background: colors.red5,
  },
  orange: {
    accent: colors.orange100,
    background: colors.orange5,
  },
  blue: {
    accent: colors.blue100,
    background: colors.blue5,
  },
};

export type CriteriaCategory = {
  // A stable identifier for the category, used as the React list key when present. Optional
  // because the admission-review source only carries one on snapshots written after OLIO-2697
  // (there is deliberately no backfill); the referral clinical value always does.
  id?: string;
  label: string;
  color: AdmissionsCriteriaColor;
  criteria: AdmissionsCriterion[];
};

// Validate an API-provided category color against the shared 5-color domain, defaulting to blue.
// Colors are configured per criteria set (not a fixed enum), so an unknown value is possible.
export function criteriaColor(color: string | null | undefined): AdmissionsCriteriaColor {
  return (color && color in colorConfig ? color : 'blue') as AdmissionsCriteriaColor;
}

// Group pre-mapped criteria under their category, preserving the first-appearance order of both
// the categories and the criteria within each. Callers order their input as needed — the
// admission job sorts by the configured order, the referral clinical value arrives pre-ordered.
export function groupCriteria(
  entries: Array<{
    categoryId?: string;
    categoryName: string;
    categoryColor: string | null | undefined;
    criterion: AdmissionsCriterion;
  }>
): CriteriaCategory[] {
  const byLabel = new Map<string, CriteriaCategory>();
  for (const { categoryId, categoryName, categoryColor, criterion } of entries) {
    let category = byLabel.get(categoryName);
    if (!category) {
      category = { id: categoryId, label: categoryName, color: criteriaColor(categoryColor), criteria: [] };
      byLabel.set(categoryName, category);
    }
    category.criteria.push(criterion);
  }
  return Array.from(byLabel.values());
}

export default function AdmissionsCriteria({ categoryLabel, color, criteria }: AdmissionsCriteriaProps) {
  const { accent, background } = colorConfig[color];

  return (
    <Card>
      <ColorBar $accent={accent} />
      <Header $background={background}>
        <HeaderText>
          {criteria.length} {categoryLabel} Criteria Found
        </HeaderText>
      </Header>
      {criteria.map((criterion, index) => (
        <div key={index}>
          {index > 0 && <Divider />}
          <CriteriaItem>
            <TitleRow>
              <Title>{criterion.label}</Title>
              {criterion.sources && criterion.sources.length > 0 && (
                <SourceBadges>
                  {criterion.sources.map((source) => (
                    <CriteriaSourceBadge
                      key={source.displayId}
                      attachmentId={source.attachmentId}
                      displayId={source.displayId}
                      attachmentType='Review::Admission'
                    />
                  ))}
                </SourceBadges>
              )}
            </TitleRow>
            {criterion.description && <Description>{criterion.description}</Description>}
          </CriteriaItem>
        </div>
      ))}
    </Card>
  );
}

// A clickable citation chip — the small numbered badge beside a criterion name that opens the
// cited source document via the shared signed-URL flow. Shared by the admission criteria cards
// and the referral clinical-assessment drawer; `size` scales the chip and `attachmentType`
// scopes the lookup to the owning model.
export function CriteriaSourceBadge({
  attachmentId,
  displayId,
  attachmentType,
  size = 16,
}: {
  attachmentId: string;
  displayId: number;
  attachmentType?: string;
  size?: number;
}) {
  const openAttachment = useClickAttachment(attachmentId, attachmentType);

  return (
    <SourceBadge type='button' $size={size} onClick={openAttachment} data-testid='criterion-source'>
      {displayId}
    </SourceBadge>
  );
}

const SourceBadge = styled.button<{ $size: number }>`
  min-width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 500;
  cursor: pointer;
  background: ${colors.white};
  color: ${colors.black100};
  border: 1px solid ${colors.black25};
  border-radius: 16px;
  padding: 0 2px;

  &:hover {
    background: ${colors.blue5};
    border-color: ${colors.blue100};
  }
`;

const Card = styled.div`
  position: relative;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  overflow: hidden;
`;

const ColorBar = styled.div<{ $accent: string }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: ${({ $accent }) => $accent};
  border-radius: 4px 0 0 4px;
`;

const Header = styled.div<{ $background: string }>`
  background: ${({ $background }) => $background};
  padding: 16px 16px 16px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderText = styled.p`
  margin: 0;
  font-family: ${textStyles.base.fontFamily};
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
`;

const CriteriaItem = styled.div`
  padding: 16px 16px 16px 24px;
`;

const Divider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${colors.black10};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Title = styled.p`
  margin: 0;
  font-family: ${textStyles.base.fontFamily};
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
`;

const SourceBadges = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
`;

const Description = styled.p`
  margin: 8px 0 0;
  font-family: ${textStyles.base.fontFamily};
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: ${colors.black75};
`;
